import React, { useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Eye, EyeOff, GripVertical, FileText, LayoutGrid, AlertTriangle, Lightbulb, Shield, Target } from 'lucide-react';
import { SectionData, DiscoveredBlock } from '../../hooks/usePdfExportStateV2';
import { PageAssignment } from '../../hooks/useTwoPhaseRender';

interface SectionSidebarProps {
  sections: SectionData[];
  pageAssignments: PageAssignment[];
  onToggleSection: (sectionId: string) => void;
  onToggleAll: (visible: boolean) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onTogglePageBreak: (sectionId: string) => void;
  pageBreaksAfter: Set<string>;
  // Granular sub-block removal
  discoveredBlocks: Map<string, DiscoveredBlock[]>;
  removedBlocks: Set<string>;
  onToggleBlock: (blockId: string) => void;
}

// Icon mapping for section types
const getSectionIcon = (type: string): React.ReactNode => {
  switch (type) {
    case 'executive-summary':
      return <FileText className="w-4 h-4" />;
    case 'analysis-matrix':
      return <LayoutGrid className="w-4 h-4" />;
    case 'key-findings':
      return <AlertTriangle className="w-4 h-4" />;
    case 'recommendations':
      return <Lightbulb className="w-4 h-4" />;
    case 'risk-mitigation-strategies':
      return <Shield className="w-4 h-4" />;
    case 'strategic-considerations':
      return <Target className="w-4 h-4" />;
    default:
      return <FileText className="w-4 h-4" />;
  }
};

// Get which page a section is on
const getSectionPage = (sectionId: string, pageAssignments: PageAssignment[]): number | null => {
  for (const page of pageAssignments) {
    if (page.sections.some(s => s.sectionId === sectionId)) {
      return page.pageNumber;
    }
  }
  return null;
};

export const SectionSidebar: React.FC<SectionSidebarProps> = ({
  sections,
  pageAssignments,
  onToggleSection,
  onToggleAll,
  onReorder,
  onTogglePageBreak,
  pageBreaksAfter,
  discoveredBlocks,
  removedBlocks,
  onToggleBlock
}) => {
  // Get visible sections sorted by orderIndex
  const sortedSections = [...sections].sort((a, b) => a.orderIndex - b.orderIndex);
  const visibleCount = sections.filter(s => s.isVisible).length;
  const allVisible = visibleCount === sections.length;
  const noneVisible = visibleCount === 0;

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;

    const fromIndex = result.source.index;
    const toIndex = result.destination.index;

    if (fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex);
    }
  }, [onReorder]);

  return (
    <div className="section-sidebar w-72 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="font-semibold text-gray-800 mb-3">Sections</h2>

        {/* Toggle all */}
        <div className="flex gap-2">
          <button
            onClick={() => onToggleAll(true)}
            disabled={allVisible}
            className={`flex-1 px-3 py-1.5 text-xs rounded transition-colors ${
              allVisible
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
            }`}
          >
            Show All
          </button>
          <button
            onClick={() => onToggleAll(false)}
            disabled={noneVisible}
            className={`flex-1 px-3 py-1.5 text-xs rounded transition-colors ${
              noneVisible
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            }`}
          >
            Hide All
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          {visibleCount} of {sections.length} sections visible
        </p>
      </div>

      {/* Draggable section list */}
      <div className="flex-1 overflow-y-auto">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sections">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`p-2 transition-colors ${
                  snapshot.isDraggingOver ? 'bg-blue-50' : ''
                }`}
              >
                {sortedSections.map((section, index) => {
                  const pageNumber = section.isVisible
                    ? getSectionPage(section.id, pageAssignments)
                    : null;
                  const hasPageBreak = pageBreaksAfter.has(section.id);

                  return (
                    <Draggable
                      key={section.id}
                      draggableId={section.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`mb-2 rounded-lg border transition-all ${
                            snapshot.isDragging
                              ? 'shadow-lg border-blue-400 bg-blue-50'
                              : section.isVisible
                                ? 'border-gray-200 bg-white hover:border-gray-300'
                                : 'border-gray-100 bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center p-2">
                            {/* Drag handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                            >
                              <GripVertical className="w-4 h-4" />
                            </div>

                            {/* Section info */}
                            <div className="flex-1 min-w-0 ml-1">
                              <div className="flex items-center gap-2">
                                <span className={section.isVisible ? 'text-gray-700' : 'text-gray-400'}>
                                  {getSectionIcon(section.type)}
                                </span>
                                <span className={`text-sm truncate ${
                                  section.isVisible ? 'text-gray-800' : 'text-gray-400'
                                }`}>
                                  {section.title}
                                </span>
                              </div>

                              {/* Page indicator and page break toggle */}
                              <div className="flex items-center gap-2 mt-1">
                                {pageNumber && (
                                  <span className="text-xs text-gray-400">
                                    Page {pageNumber}
                                  </span>
                                )}
                                {section.isVisible && (
                                  <button
                                    onClick={() => onTogglePageBreak(section.id)}
                                    className={`text-xs px-1.5 py-0.5 rounded transition-colors ${
                                      hasPageBreak
                                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                    }`}
                                    title={hasPageBreak ? 'Remove page break after' : 'Add page break after'}
                                  >
                                    {hasPageBreak ? '⊟ Break' : '⊞ Break'}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Visibility toggle */}
                            <button
                              onClick={() => onToggleSection(section.id)}
                              className={`p-1.5 rounded transition-colors ${
                                section.isVisible
                                  ? 'text-blue-600 hover:bg-blue-50'
                                  : 'text-gray-400 hover:bg-gray-100'
                              }`}
                              title={section.isVisible ? 'Hide section' : 'Show section'}
                            >
                              {section.isVisible ? (
                                <Eye className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                            </button>
                          </div>

                          {/* Page break indicator line */}
                          {hasPageBreak && section.isVisible && (
                            <div className="mx-2 mb-2 border-t-2 border-dashed border-orange-400 relative">
                              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-1 text-xs text-orange-600">
                                Page Break
                              </span>
                            </div>
                          )}

                          {/* Removable sub-blocks discovered within this section */}
                          {section.isVisible && (discoveredBlocks.get(section.id)?.length ?? 0) > 0 && (
                            <div className="ml-7 mr-2 mb-2 pl-2 border-l border-gray-200 space-y-0.5">
                              {discoveredBlocks.get(section.id)!.map(block => {
                                const blockVisible = !removedBlocks.has(block.id);
                                return (
                                  <div key={block.id} className="flex items-center gap-1.5 py-0.5">
                                    <span className={`flex-1 text-xs truncate ${blockVisible ? 'text-gray-600' : 'text-gray-400 line-through'}`}>
                                      {block.title}
                                    </span>
                                    <button
                                      onClick={() => onToggleBlock(block.id)}
                                      className={`p-1 rounded transition-colors ${
                                        blockVisible ? 'text-blue-500 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'
                                      }`}
                                      title={blockVisible ? 'Remove this block from the PDF' : 'Restore this block'}
                                    >
                                      {blockVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Help text */}
      <div className="p-3 bg-gray-50 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Drag sections to reorder • Click eye to toggle visibility
        </p>
      </div>
    </div>
  );
};

export default SectionSidebar;
